import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import {
  buildAuditModel,
  buildRunMatrixModel,
  type DagAuditEvent,
  type DagRunMatrixRecord,
} from "./viewProjections";
import type { DagViewerGraph } from "./types";
import "./dag-table-views.css";

export interface DagTableViewProps {
  graph: DagViewerGraph;
  selectedId?: string | null;
  onSelect?: (nodeId: string | null) => void;
  onActivate?: (nodeId: string) => void;
}

type AuditOrder = "newest" | "oldest";

const RUN_ROW_PAGE_SIZE = 18;
const RUN_COLUMN_PAGE_SIZE = 12;
const HISTORY_POSITION_PAGE_SIZE = 12;
const AUDIT_PAGE_SIZE = 50;

interface OperationHistoryRecord {
  nodeId: string;
  nodeIndex: number;
  familyId: string;
  familyLabel: string;
  state: string | null;
  timestamp: string | null;
}

interface OperationHistoryRow {
  id: string;
  label: string;
  records: OperationHistoryRecord[];
}

interface OperationHistoryModel {
  rows: OperationHistoryRow[];
  candidateCount: number;
  includedCount: number;
}

const OPERATION_FAMILY_KEYS = [
  "operation_spec_id",
  "operation_spec",
  "spec_id",
  "operation_family_id",
  "operation_family",
  "operation_kind",
  "operation_type",
] as const;

const OPERATION_FAMILY_LABEL_KEYS = [
  "operation_spec_label",
  "operation_spec_name",
  "spec_name",
  "operation_family_label",
] as const;

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function explicitText(
  sources: ReadonlyArray<Record<string, unknown>>,
  keys: ReadonlyArray<string>,
): string | null {
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
    }
  }
  return null;
}

function operationHistories(graph: DagViewerGraph): OperationHistoryModel {
  const rows = new Map<string, OperationHistoryRow>();
  let candidateCount = 0;
  let includedCount = 0;

  graph.nodes.forEach((node, nodeIndex) => {
    if (node.type !== "operation") return;
    candidateCount += 1;
    const raw = recordValue(node.raw);
    const sources = [node.metadata, recordValue(raw.metadata), raw];
    const explicitFamily = explicitText(sources, OPERATION_FAMILY_KEYS);
    const normalizedKind = node.kind.trim();
    const familyId = explicitFamily
      ?? (!/^(?:operation(?:\.run)?|record|unknown)$/i.test(normalizedKind) ? normalizedKind : null);
    if (!familyId) return;
    includedCount += 1;
    const familyLabel = explicitText(sources, OPERATION_FAMILY_LABEL_KEYS) ?? familyId;
    let row = rows.get(familyId);
    if (!row) {
      row = { id: familyId, label: familyLabel, records: [] };
      rows.set(familyId, row);
    }
    row.records.push({
      nodeId: node.id,
      nodeIndex,
      familyId,
      familyLabel,
      state: node.state,
      timestamp: node.timestamp,
    });
  });

  const orderedRows = [...rows.values()].sort((left, right) =>
    left.label.localeCompare(right.label) || left.id.localeCompare(right.id));
  for (const row of orderedRows) {
    row.records.sort((left, right) => {
      const leftTime = parsedTime(left.timestamp) ?? Number.POSITIVE_INFINITY;
      const rightTime = parsedTime(right.timestamp) ?? Number.POSITIVE_INFINITY;
      return leftTime - rightTime || left.nodeId.localeCompare(right.nodeId);
    });
  }
  return { rows: orderedRows, candidateCount, includedCount };
}

function parsedTime(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDateTime(value: string | null): string {
  const parsed = parsedTime(value);
  if (parsed == null) return "Time not recorded";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(parsed));
}

function shortIdentifier(value: string): string {
  if (/^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(value)) return `${value.slice(0, 8)}…`;
  return value.length > 24 ? `${value.slice(0, 22)}…` : value;
}

function statusTone(state: string | null): "good" | "bad" | "pending" | "neutral" {
  const normalized = state?.toLowerCase() ?? "";
  if (/fail|error|invalid|reject|cancel|stale|supersed|deleted/.test(normalized)) return "bad";
  if (/complete|success|ready|active|published|valid/.test(normalized)) return "good";
  if (/pending|queued|running|progress|waiting/.test(normalized)) return "pending";
  return "neutral";
}

function activateWithKeyboard(
  event: ReactKeyboardEvent<HTMLElement>,
  id: string,
  onActivate?: (nodeId: string) => void,
): void {
  if (event.key !== "Enter" || (!event.ctrlKey && !event.metaKey) || !onActivate) return;
  event.preventDefault();
  event.stopPropagation();
  onActivate(id);
}

function Pager({
  label,
  page,
  pageCount,
  onPage,
}: {
  label: string;
  page: number;
  pageCount: number;
  onPage: (page: number) => void;
}) {
  return (
    <nav className="dag-table-view__pager" aria-label={`${label} pagination`}>
      <button
        type="button"
        disabled={page <= 0}
        onClick={() => onPage(Math.max(0, page - 1))}
        aria-label={`Previous ${label.toLowerCase()} page`}
      >
        ←
      </button>
      <output aria-live="polite">Page {page + 1} of {Math.max(1, pageCount)}</output>
      <button
        type="button"
        disabled={page >= pageCount - 1}
        onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        aria-label={`Next ${label.toLowerCase()} page`}
      >
        →
      </button>
    </nav>
  );
}

function runCellLabel(
  graph: DagViewerGraph,
  record: DagRunMatrixRecord,
  familyLabel: string,
  runLabel: string,
): string {
  const node = graph.nodes[record.sourceIndex];
  return [
    `Select ${node?.label ?? record.nodeId}`,
    `family ${familyLabel}`,
    `run ${runLabel}`,
    record.state ?? "state not recorded",
    formatDateTime(record.timestamp),
  ].join(", ");
}

function OperationHistoryView({
  graph,
  selectedId,
  onSelect,
  onActivate,
}: DagTableViewProps) {
  const titleId = useId();
  const model = useMemo(() => operationHistories(graph), [graph]);
  const [rowPage, setRowPage] = useState(0);
  const [positionPage, setPositionPage] = useState(0);
  const rowPageCount = Math.max(1, Math.ceil(model.rows.length / RUN_ROW_PAGE_SIZE));
  const largestHistory = model.rows.reduce((largest, row) => Math.max(largest, row.records.length), 0);
  const positionPageCount = Math.max(1, Math.ceil(largestHistory / HISTORY_POSITION_PAGE_SIZE));
  const visibleRows = model.rows.slice(
    rowPage * RUN_ROW_PAGE_SIZE,
    (rowPage + 1) * RUN_ROW_PAGE_SIZE,
  );
  const positionStart = positionPage * HISTORY_POSITION_PAGE_SIZE;
  const positionEnd = Math.min(largestHistory, positionStart + HISTORY_POSITION_PAGE_SIZE);
  const coveragePercent = model.candidateCount
    ? Math.round((model.includedCount / model.candidateCount) * 100)
    : 0;

  useEffect(() => {
    setRowPage(0);
    setPositionPage(0);
  }, [graph]);

  useEffect(() => {
    if (rowPage >= rowPageCount) setRowPage(rowPageCount - 1);
    if (positionPage >= positionPageCount) setPositionPage(positionPageCount - 1);
  }, [positionPage, positionPageCount, rowPage, rowPageCount]);

  return (
    <section className="dag-table-view dag-run-matrix" aria-labelledby={titleId} data-testid="dag-run-matrix" data-view="operation-histories">
      <header className="dag-table-view__header">
        <div>
          <span>Repeated work</span>
          <h2 id={titleId}>Operation histories</h2>
          <p>The graph has no paired family/run IDs. Rows therefore use recorded operation-spec identity or operation kind, and positions are ordered independently inside each row.</p>
        </div>
      </header>

      <div className={`dag-table-view__coverage${coveragePercent < 100 ? " is-reduced" : ""}`} role="status">
        <div>
          <strong>{coveragePercent}% operation identity coverage</strong>
          <span>{model.includedCount} of {model.candidateCount} operation records carry a usable recorded spec or kind.</span>
        </div>
        <dl>
          <div><dt>Families</dt><dd>{model.rows.length}</dd></div>
          <div><dt>Operations</dt><dd>{model.includedCount}</dd></div>
          <div><dt>Longest history</dt><dd>{largestHistory}</dd></div>
        </dl>
      </div>

      <p className="dag-table-view__honesty-note" role="note">
        Position 2 in one row is not the same run as position 2 in another row. This view shows each operation family&apos;s local chronology; it does not invent cross-family revision alignment.
      </p>

      {model.rows.length ? (
        <>
          <div className="dag-table-view__scroller" tabIndex={0} aria-label="Scrollable operation histories">
            <table>
              <caption className="dag-table-view__sr-only">
                Operation records grouped by recorded spec or kind. Position columns are independently ordered inside each family and are not shared run identities.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Operation family</th>
                  {Array.from({ length: Math.max(0, positionEnd - positionStart) }, (_, offset) => (
                    <th scope="col" key={positionStart + offset}>Position {positionStart + offset + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row" title={row.id}>
                      <strong>{row.label}</strong>
                      <small>{row.records.length} {row.records.length === 1 ? "record" : "records"}</small>
                    </th>
                    {Array.from({ length: Math.max(0, positionEnd - positionStart) }, (_, offset) => {
                      const position = positionStart + offset;
                      const record = row.records[position];
                      const node = record ? graph.nodes[record.nodeIndex] : null;
                      const isSelected = record?.nodeId === selectedId;
                      return (
                        <td key={position}>
                          {record && node ? (
                            <button
                              type="button"
                              className={`dag-run-matrix__cell is-${statusTone(record.state)}${isSelected ? " is-selected" : ""}`}
                              aria-pressed={isSelected}
                              aria-label={`Select ${node.label}, ${row.label}, family-local position ${position + 1}, ${record.state ?? "state not recorded"}, ${formatDateTime(record.timestamp)}`}
                              title="Select; double-click or Ctrl/Command+Enter to open"
                              onClick={() => onSelect?.(record.nodeId)}
                              onDoubleClick={() => onActivate?.(record.nodeId)}
                              onKeyDown={(event) => activateWithKeyboard(event, record.nodeId, onActivate)}
                            >
                              <i aria-hidden="true" />
                              <strong>{node.label}</strong>
                              <span>{record.state ?? "State not recorded"}</span>
                              <small>{formatDateTime(record.timestamp)}</small>
                            </button>
                          ) : <span className="dag-run-matrix__empty" aria-label="No operation recorded at this family-local position">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="dag-table-view__footer">
            <Pager label="Operation families" page={rowPage} pageCount={rowPageCount} onPage={setRowPage} />
            <span>{positionStart + 1}–{Math.max(positionStart + 1, positionEnd)} of {largestHistory} family-local positions</span>
            <Pager label="History positions" page={positionPage} pageCount={positionPageCount} onPage={setPositionPage} />
          </footer>
        </>
      ) : (
        <div className="dag-table-view__empty-state" role="status">
          <strong>No operation histories</strong>
          <p>No operation record carries a usable explicit spec or normalized operation kind.</p>
        </div>
      )}
    </section>
  );
}

/**
 * A true family-by-run matrix. Every column is a single global run identity,
 * so cells in different rows remain directly comparable rather than using a
 * row-relative "first run / second run" position.
 */
export function RunMatrixView({ graph, selectedId = null, onSelect, onActivate }: DagTableViewProps) {
  const titleId = useId();
  const model = useMemo(() => buildRunMatrixModel(graph), [graph]);
  const [rowPage, setRowPage] = useState(0);
  const [columnPage, setColumnPage] = useState(0);
  const rowPageCount = Math.max(1, Math.ceil(model.rows.length / RUN_ROW_PAGE_SIZE));
  const columnPageCount = Math.max(1, Math.ceil(model.columns.length / RUN_COLUMN_PAGE_SIZE));
  const visibleRows = model.rows.slice(
    rowPage * RUN_ROW_PAGE_SIZE,
    (rowPage + 1) * RUN_ROW_PAGE_SIZE,
  );
  const columnStart = columnPage * RUN_COLUMN_PAGE_SIZE;
  const visibleColumns = model.columns.slice(columnStart, columnStart + RUN_COLUMN_PAGE_SIZE);
  const coveragePercent = model.coverage.totalNodes
    ? Math.round(model.coverage.ratio * 100)
    : 0;
  const cellByCoordinate = useMemo(() => {
    const cells = new Map<string, (typeof model.rows)[number]["cells"][number]>();
    for (const row of model.rows) {
      for (const cell of row.cells) cells.set(`${row.id}\u0000${cell.runId}`, cell);
    }
    return cells;
  }, [model]);

  useEffect(() => {
    setRowPage(0);
    setColumnPage(0);
  }, [graph]);

  useEffect(() => {
    if (rowPage >= rowPageCount) setRowPage(rowPageCount - 1);
    if (columnPage >= columnPageCount) setColumnPage(columnPageCount - 1);
  }, [columnPage, columnPageCount, rowPage, rowPageCount]);

  if (!model.rows.length || !model.columns.length) {
    return (
      <OperationHistoryView
        graph={graph}
        selectedId={selectedId}
        onSelect={onSelect}
        onActivate={onActivate}
      />
    );
  }

  return (
    <section className="dag-table-view dag-run-matrix" aria-labelledby={titleId} data-testid="dag-run-matrix">
      <header className="dag-table-view__header">
        <div>
          <span>Repeated work</span>
          <h2 id={titleId}>Run identity matrix</h2>
          <p>Rows are explicit families; columns are global run or revision identities shared by every row. Missing identifiers are never inferred from labels.</p>
        </div>
      </header>

      <div className={`dag-table-view__coverage${model.coverage.excludedNodes ? " is-reduced" : ""}`} role="status">
        <div>
          <strong>{coveragePercent}% paired identity coverage</strong>
          <span>{model.coverage.includedNodes} of {model.coverage.totalNodes} source records carry both an explicit family and run identifier.</span>
        </div>
        <dl>
          <div><dt>Family ID</dt><dd>{model.coverage.nodesWithFamily}/{model.coverage.totalNodes}</dd></div>
          <div><dt>Run ID</dt><dd>{model.coverage.nodesWithRun}/{model.coverage.totalNodes}</dd></div>
          <div><dt>Matrix</dt><dd>{model.rows.length} × {model.columns.length}</dd></div>
        </dl>
      </div>

      {model.coverage.excludedNodes > 0 ? (
        <p className="dag-table-view__honesty-note" role="note">
          {model.coverage.excludedNodes} records remain outside the matrix: {model.coverage.missingFamily} missing family,
          {" "}{model.coverage.missingRun} missing run, and {model.coverage.missingBoth} missing both identifiers.
        </p>
      ) : null}

      <>
          <div className="dag-table-view__scroller" tabIndex={0} aria-label="Scrollable run identity matrix">
            <table>
              <caption className="dag-table-view__sr-only">
                Explicit run records arranged by family rows and globally consistent run identity columns.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Family</th>
                  {visibleColumns.map((column) => (
                    <th scope="col" key={column.id} title={column.id}>
                      {shortIdentifier(column.label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row" title={row.id}>
                      <strong>{row.label}</strong>
                      <small>{row.recordCount} {row.recordCount === 1 ? "record" : "records"}</small>
                    </th>
                    {visibleColumns.map((column) => {
                      const cell = cellByCoordinate.get(`${row.id}\u0000${column.id}`);
                      return (
                        <td key={column.id}>
                          {cell ? cell.records.map((record) => {
                            const node = graph.nodes[record.sourceIndex];
                            const isSelected = selectedId === record.nodeId;
                            return (
                              <button
                                type="button"
                                key={record.nodeId}
                                className={`dag-run-matrix__cell is-${statusTone(record.state)}${isSelected ? " is-selected" : ""}`}
                                aria-pressed={isSelected}
                                aria-label={runCellLabel(graph, record, row.label, column.label)}
                                title="Select; double-click or Ctrl/Command+Enter to open"
                                onClick={() => onSelect?.(record.nodeId)}
                                onDoubleClick={() => onActivate?.(record.nodeId)}
                                onKeyDown={(event) => activateWithKeyboard(event, record.nodeId, onActivate)}
                              >
                                <i aria-hidden="true" />
                                <strong>{node?.label ?? shortIdentifier(record.nodeId)}</strong>
                                <span>{record.state ?? "State not recorded"}</span>
                                <small>{formatDateTime(record.timestamp)}</small>
                              </button>
                            );
                          }) : <span className="dag-run-matrix__empty" aria-label="No recorded run for this family and run identity">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="dag-table-view__footer">
            <Pager label="Families" page={rowPage} pageCount={rowPageCount} onPage={setRowPage} />
            <span>{columnStart + 1}–{columnStart + visibleColumns.length} of {model.columns.length} global run identities</span>
            <Pager label="Run identities" page={columnPage} pageCount={columnPageCount} onPage={setColumnPage} />
          </footer>
      </>
    </section>
  );
}

function auditEventType(event: DagAuditEvent): string {
  if (!event.eventType) return event.timeSemantics === "recorded" ? "Recorded" : "Event";
  const spaced = event.eventType.replace(/[._-]+/g, " ").replace(/\s+/g, " ").trim();
  return spaced ? `${spaced.charAt(0).toLocaleUpperCase()}${spaced.slice(1)}` : "Event";
}

function orderedAuditEvents(events: DagAuditEvent[], order: AuditOrder): DagAuditEvent[] {
  return [...events].sort((left, right) => {
    const leftTime = Date.parse(left.timestamp);
    const rightTime = Date.parse(right.timestamp);
    const timeOrder = order === "newest" ? rightTime - leftTime : leftTime - rightTime;
    return timeOrder || left.id.localeCompare(right.id);
  });
}

function AuditDetails({
  graph,
  event,
  onSelect,
  onActivate,
}: {
  graph: DagViewerGraph;
  event: DagAuditEvent;
  onSelect?: (nodeId: string | null) => void;
  onActivate?: (nodeId: string) => void;
}) {
  const node = graph.nodes[event.sourceIndex]!;
  const parents = (graph.incoming[event.sourceIndex] ?? []).map((index) => graph.nodes[index]!);
  const children = (graph.outgoing[event.sourceIndex] ?? []).map((index) => graph.nodes[index]!);
  return (
    <aside className="dag-audit__details" aria-label="Audit event details" data-testid="dag-audit-details">
      <header>
        <div><span>{auditEventType(event)}</span><h3>{event.label}</h3></div>
        <button type="button" aria-label="Close audit details" onClick={() => onSelect?.(null)}>×</button>
      </header>
      <div className="dag-audit__badges">
        <span>{node.type}</span>
        <span>{node.kind}</span>
        <span>{event.timeSemantics === "event" ? "Event time" : "Recorded time"}</span>
        <span className={`is-${statusTone(event.state)}`}>{event.state ?? "State not recorded"}</span>
      </div>
      <dl className="dag-audit__metadata">
        <div><dt>When</dt><dd>{formatDateTime(event.timestamp)}</dd></div>
        <div><dt>Time source</dt><dd><code>{event.timestampSource}</code></dd></div>
        <div><dt>Actor</dt><dd>{event.actor ?? "Actor not recorded"}</dd></div>
        <div><dt>Record ID</dt><dd><code>{shortIdentifier(event.nodeId)}</code></dd></div>
        <div><dt>Digest</dt><dd><code>{node.digest ? shortIdentifier(node.digest) : "Not recorded"}</code></dd></div>
        <div><dt>Upstream</dt><dd>{parents.length}</dd></div>
        <div><dt>Downstream</dt><dd>{children.length}</dd></div>
      </dl>
      {parents.length || children.length ? (
        <section className="dag-audit__context" aria-label="Immediate lineage context">
          <h4>Immediate context</h4>
          {parents.slice(0, 6).map((parent) => (
            <button type="button" key={`parent:${parent.id}`} onClick={() => onSelect?.(parent.id)}>
              <span>Upstream</span><strong>{parent.label}</strong>
            </button>
          ))}
          {children.slice(0, 6).map((child) => (
            <button type="button" key={`child:${child.id}`} onClick={() => onSelect?.(child.id)}>
              <span>Downstream</span><strong>{child.label}</strong>
            </button>
          ))}
        </section>
      ) : null}
      {onActivate ? (
        <button className="dag-table-view__open" type="button" onClick={() => onActivate(event.nodeId)}>Open record ↗</button>
      ) : null}
    </aside>
  );
}

export function AuditView({ graph, selectedId = null, onSelect, onActivate }: DagTableViewProps) {
  const titleId = useId();
  const [order, setOrder] = useState<AuditOrder>("newest");
  const [page, setPage] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const model = useMemo(() => buildAuditModel(graph), [graph]);
  const events = useMemo(() => orderedAuditEvents(model.events, order), [model.events, order]);
  const pageCount = Math.max(1, Math.ceil(events.length / AUDIT_PAGE_SIZE));
  const visibleEvents = events.slice(page * AUDIT_PAGE_SIZE, (page + 1) * AUDIT_PAGE_SIZE);
  const selectedEvent = selectedId
    ? events.find((event) => event.id === selectedEventId && event.nodeId === selectedId)
      ?? events.find((event) => event.nodeId === selectedId)
      ?? null
    : null;
  const actorCoverage = events.reduce((count, event) => count + Number(Boolean(event.actor)), 0);

  useEffect(() => setPage(0), [graph, order]);
  useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);
  useEffect(() => {
    if (!selectedId || !model.events.some((event) => event.id === selectedEventId && event.nodeId === selectedId)) {
      setSelectedEventId(null);
    }
  }, [model.events, selectedEventId, selectedId]);

  const selectNode = (nodeId: string | null) => {
    setSelectedEventId(null);
    onSelect?.(nodeId);
  };

  return (
    <section className="dag-table-view dag-audit" aria-labelledby={titleId} data-testid="dag-audit-view">
      <header className="dag-table-view__header">
        <div>
          <span>Chronological record</span>
          <h2 id={titleId}>Audit</h2>
          <p>Explicit event facts remain distinct; timestamped records without an event type are labelled “Recorded.” Missing or invalid time facts are reported, not reconstructed.</p>
        </div>
        <div className="dag-table-view__segmented" role="group" aria-label="Audit order">
          <button type="button" aria-pressed={order === "newest"} onClick={() => setOrder("newest")}>Newest first</button>
          <button type="button" aria-pressed={order === "oldest"} onClick={() => setOrder("oldest")}>Oldest first</button>
        </div>
      </header>

      <div className={`dag-table-view__coverage${model.coverage.excludedNodes ? " is-reduced" : ""}`} role="status">
        <div>
          <strong>{events.length} dated {events.length === 1 ? "entry" : "entries"} from {model.coverage.includedNodes}/{model.coverage.totalNodes} records</strong>
          <span>A record can contribute more than one entry when it contains distinct explicit events.</span>
        </div>
        <dl>
          <div><dt>Event facts</dt><dd>{model.coverage.eventEntries}</dd></div>
          <div><dt>Recorded</dt><dd>{model.coverage.recordedEntries}</dd></div>
          <div><dt>Actor</dt><dd>{actorCoverage}/{events.length}</dd></div>
        </dl>
      </div>

      {model.coverage.excludedNodes > 0 ? (
        <p className="dag-table-view__honesty-note" role="note">
          {model.untimedNodeIds.length} records have no timestamp; {model.invalidTimestampNodeIds.length} contain only invalid timestamps.
          These {model.coverage.excludedNodes} records are excluded from the dated audit table.
        </p>
      ) : null}

      <div className={`dag-audit__layout${selectedEvent ? " has-selection" : ""}`}>
        <div className="dag-table-view__scroller" tabIndex={0} aria-label="Scrollable audit table">
          <table>
            <caption className="dag-table-view__sr-only">
              Lineage audit entries ordered {order === "newest" ? "newest to oldest" : "oldest to newest"}.
            </caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Event</th>
                <th scope="col">Record</th>
                <th scope="col">Actor</th>
                <th scope="col">State</th>
              </tr>
            </thead>
            <tbody>
              {visibleEvents.map((event) => {
                const node = graph.nodes[event.sourceIndex]!;
                const isSelected = selectedEvent?.id === event.id;
                return (
                  <tr
                    key={event.id}
                    className={isSelected ? "is-selected" : ""}
                    data-testid="dag-audit-row"
                  >
                    <td><time dateTime={event.timestamp}>{formatDateTime(event.timestamp)}</time></td>
                    <td><strong>{auditEventType(event)}</strong></td>
                    <td>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={`Select audit event ${event.label} for ${node.label}`}
                        onClick={() => {
                          setSelectedEventId(event.id);
                          onSelect?.(event.nodeId);
                        }}
                        onDoubleClick={() => onActivate?.(event.nodeId)}
                        onKeyDown={(keyboardEvent) => activateWithKeyboard(keyboardEvent, event.nodeId, onActivate)}
                      >
                        {node.label}
                      </button>
                      <small>{event.timeSemantics === "event" ? event.label : node.kind}</small>
                    </td>
                    <td title={event.actor ?? undefined}>{event.actor ? shortIdentifier(event.actor) : <span className="dag-table-view__missing">Not recorded</span>}</td>
                    <td><span className={`dag-audit__state is-${statusTone(event.state)}`}><i aria-hidden="true" />{event.state ?? "Not recorded"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!events.length ? (
            <div className="dag-table-view__empty-state" role="status">
              <strong>No dated audit entries</strong>
              <p>The graph is empty or none of its records contains a valid explicit or recorded timestamp.</p>
            </div>
          ) : null}
        </div>
        {selectedEvent ? (
          <AuditDetails
            graph={graph}
            event={selectedEvent}
            onSelect={selectNode}
            onActivate={onActivate}
          />
        ) : null}
      </div>

      {events.length ? (
        <footer className="dag-table-view__footer">
          <span>{page * AUDIT_PAGE_SIZE + 1}–{Math.min(events.length, (page + 1) * AUDIT_PAGE_SIZE)} of {events.length} entries</span>
          <Pager label="Audit entries" page={page} pageCount={pageCount} onPage={setPage} />
        </footer>
      ) : null}
    </section>
  );
}
